import {
  BUDGET_CAPTURE_SHADOW_OPACITY,
  BUDGET_CAPTURE_SHADOW_RADIUS,
  SCENE_SHADOW_CAMERA_HALF_EXTENT,
} from '@constants'
import {
  Box3,
  DirectionalLight,
  Material,
  Mesh,
  OrthographicCamera,
  Scene,
  ShadowMaterial,
  Sphere,
  Vector3,
  WebGLRenderer,
} from 'three'

type MaterialOpacitySnapshot = {
  material: ShadowMaterial
  opacity: number
}

type LightSnapshot = {
  light: DirectionalLight
  target: Vector3
  radius: number
  shadowCam: {
    left: number
    right: number
    top: number
    bottom: number
    near: number
    far: number
  } | null
}

const _size = new Vector3()
const _sphere = new Sphere()

/**
 * Prepara sombras sutiles para la captura de presupuesto:
 * - baja opacity del shadowMaterial de suelo
 * - suaviza radius de la key light
 * - recentra el shadow map en el conjunto
 * Restaura todo al terminar.
 */
export function prepareBudgetCaptureSubtleShadows(
  scene: Scene,
  gl: WebGLRenderer,
  focus: Vector3,
  box: Box3,
): () => void {
  const prevShadowEnabled = gl.shadowMap.enabled
  gl.shadowMap.enabled = true

  const materialSnaps: MaterialOpacitySnapshot[] = []
  const lightSnaps: LightSnapshot[] = []

  box.getSize(_size)
  box.getBoundingSphere(_sphere)
  const radius = Math.max(_sphere.radius, 1)
  const half = Math.max(
    SCENE_SHADOW_CAMERA_HALF_EXTENT,
    radius * 1.35,
    Math.max(_size.x, _size.z) * 0.55 + 1200,
  )

  scene.traverse((obj) => {
    const mesh = obj as Mesh
    if (mesh.isMesh) {
      const materials = (
        Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      ) as Material[]
      for (const mat of materials) {
        if (!mat || !(mat as ShadowMaterial).isShadowMaterial) continue
        const shadowMat = mat as ShadowMaterial
        materialSnaps.push({ material: shadowMat, opacity: shadowMat.opacity })
        shadowMat.opacity = BUDGET_CAPTURE_SHADOW_OPACITY
      }
    }

    const light = obj as DirectionalLight
    if (!light.isDirectionalLight || !light.castShadow) return

    const snap: LightSnapshot = {
      light,
      target: light.target.position.clone(),
      radius: light.shadow.radius,
      shadowCam: null,
    }

    light.target.position.copy(focus)
    light.target.updateMatrixWorld()
    light.shadow.radius = BUDGET_CAPTURE_SHADOW_RADIUS
    light.shadow.needsUpdate = true

    const cam = light.shadow?.camera as OrthographicCamera | undefined
    if (cam?.isOrthographicCamera) {
      snap.shadowCam = {
        left: cam.left,
        right: cam.right,
        top: cam.top,
        bottom: cam.bottom,
        near: cam.near,
        far: cam.far,
      }
      cam.left = -half
      cam.right = half
      cam.top = half
      cam.bottom = -half
      cam.near = 10
      cam.far = Math.max(28000, half * 6)
      cam.updateProjectionMatrix()
    }

    lightSnaps.push(snap)
  })

  gl.shadowMap.needsUpdate = true

  return () => {
    gl.shadowMap.enabled = prevShadowEnabled
    gl.shadowMap.needsUpdate = true

    for (const snap of materialSnaps) {
      snap.material.opacity = snap.opacity
    }

    for (const snap of lightSnaps) {
      snap.light.target.position.copy(snap.target)
      snap.light.target.updateMatrixWorld()
      snap.light.shadow.radius = snap.radius
      snap.light.shadow.needsUpdate = true
      const cam = snap.light.shadow?.camera as OrthographicCamera | undefined
      if (cam?.isOrthographicCamera && snap.shadowCam) {
        cam.left = snap.shadowCam.left
        cam.right = snap.shadowCam.right
        cam.top = snap.shadowCam.top
        cam.bottom = snap.shadowCam.bottom
        cam.near = snap.shadowCam.near
        cam.far = snap.shadowCam.far
        cam.updateProjectionMatrix()
      }
    }
  }
}
